// Sticky Header

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Calls when Component is Initialized in DOM
   *
   */
  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
    this.headerBounds = {};
    this.currentScrollTop = 0;

    this.onScrollHandler = this.onScroll.bind(this);
    this.onClickOutsideHandler = this.handleClickOutside.bind(this);

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('header-sticky');
    };

    window.addEventListener('scroll', this.onScrollHandler, false);
    document.addEventListener('click', this.onClickOutsideHandler, true);

    // Get all sticky announcement bars
    this.announcementBars = document.querySelectorAll('[data-top-bar][data-sticky="true"]');
    this.updateAnnouncementHeight = this.updateAnnouncementHeight.bind(this);

    if (this.announcementBars.length > 0) {
      this.announcementBarHeight = this.calculateTotalAnnouncementHeight();
      this.announcementResizeObserver = new ResizeObserver(this.updateAnnouncementHeight);

      // Observe all sections (to detect when sections above are added/removed/changed)
      const allSections = document.querySelectorAll('[id^="shopify-section-"]');
      allSections.forEach(section => {
        this.announcementResizeObserver.observe(section);
      });

      // Also observe announcement bars specifically
      this.announcementBars.forEach(bar => {
        this.announcementResizeObserver.observe(bar);
      });

      // Only set initial position if header is always sticky
      // Otherwise, wait until header becomes sticky on scroll
      if (this.headerIsAlwaysSticky) {
        // Use requestAnimationFrame to ensure DOM is fully rendered before calculating
        requestAnimationFrame(() => {
          this.updateAnnouncementHeight();
        });
      }
    } else {
      this.announcementBarHeight = 0;
      // Only set position if header is always sticky
      if (this.headerIsAlwaysSticky) {
        this.setHeaderPosition(0);
      }
    }

    window.addEventListener('resize', this.updateAnnouncementHeight, false);
    this.createObserver();
  }

  /**
   * Calls when Component is removed from DOM
   *
   */
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    document.removeEventListener('click', this.onClickOutsideHandler, true);
    window.removeEventListener('resize', this.updateAnnouncementHeight);
    if (this.announcementResizeObserver) this.announcementResizeObserver.disconnect();
  }

  /**
   * To observe Sticky header visibility on User Scroll
   *
   */
  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  handleClickOutside(event) {
    // If click is outside the header and dropdowns
    const isInsideHeader = this.header.contains(event.target);
    const isInsideDropdown = event.target.closest('.dropdown.open') !== null;

    if (!isInsideHeader && !isInsideDropdown) {
      this.closeMenuDropdowns();
    }
  }

  /**
   * Window Scroll event
   *
   */
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // console.log(scrollTop, this.currentScrollTop, this.headerBounds.bottom);
    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      requestAnimationFrame(this.reveal.bind(this));
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  /**
   * Hide Sticky header
   * Close Menu Drawer / Seach Modal / Cart Drawer
   *
   */
  hide() {
    if (!this.headerIsAlwaysSticky) {
      this.header.classList.add('header-hidden', 'header-sticky');
      this.closeSearchModal();
      this.closeMenuDropdowns();
    }
    // Keep header positioned below the announcement bar while scrolling
    this.setHeaderPosition(this.announcementBarHeight);
  }

  /**
   * Show Sticky Header
   *
   */
  reveal() {
    if (!this.headerIsAlwaysSticky) {
      this.header.classList.add('header-sticky', 'animate');
      this.header.classList.remove('header-hidden');
    }
    this.setHeaderPosition(this.announcementBarHeight);
  }

  reset() {
    if (this.headerIsAlwaysSticky) {
      // Keep header positioned below announcement bars when always sticky
      this.setHeaderPosition(this.announcementBarHeight);
      return;
    }
    // Remove sticky classes and reset position to normal flow
    this.header.classList.remove('header-hidden', 'header-sticky', 'animate');
    // Remove top positioning so header returns to normal flow
    this.header.style.top = '';
  }

  /**
   * Set header position with smooth transition
   * @param {number} position - The top position value in pixels
   */
  setHeaderPosition(position) {
    if (!this.header) return;

    // Only apply positioning when header is sticky
    const isSticky = this.header.classList.contains('header-sticky') || this.headerIsAlwaysSticky;
    if (!isSticky) {
      this.header.style.top = '';
      return;
    }

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // On mobile, keep header at top (0) and push announcement bars below header
      this.header.style.top = `0px`;
      if (this.announcementBars.length > 0) {
        let cumulativeTop = this.header.offsetHeight;
        this.announcementBars.forEach(bar => {
          const sectionId = bar.dataset.sectionId;
          const section = document.getElementById(`shopify-section-${sectionId}`);
          if (section && position > 0) {
            section.style.top = `${cumulativeTop}px`;
            cumulativeTop += bar.offsetHeight;
          } else if (section) {
            section.style.top = `0px`;
          }
        });
      }
    } else {
      // On desktop/tablet, position header below announcement and clear mobile positioning
      if (this.announcementBars.length > 0) {
        this.announcementBars.forEach(bar => {
          const sectionId = bar.dataset.sectionId;
          const section = document.getElementById(`shopify-section-${sectionId}`);
          // Let the announcement bar stacking script handle positioning
        });
      }
      this.header.style.top = `${position}px`;
    }
  }

  /**
   * Calculate height of all sections/blocks that appear before a given section
   * @param {HTMLElement} targetSection - The section to find preceding elements for
   * @returns {number} - Total height of preceding sections
   */
  calculatePrecedingSectionsHeight(targetSection) {
    if (!targetSection) return 0;

    let totalHeight = 0;
    const allSections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
    const targetIndex = allSections.indexOf(targetSection);

    // If section not found, return 0
    if (targetIndex === -1) return 0;

    // Calculate height of all sections before this one
    for (let i = 0; i < targetIndex; i++) {
      const precedingSection = allSections[i];

      // Only count visible sections (not disabled/hidden)
      if (precedingSection && precedingSection.offsetHeight > 0) {
        // Check if section is actually visible (not display: none)
        const computedStyle = window.getComputedStyle(precedingSection);
        if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden') {
          totalHeight += precedingSection.offsetHeight || 0;
        }
      }
    }

    return totalHeight;
  }

  /**
   * Calculate total height of all sticky announcement bars
   * Only counts sticky announcement bars that appear before the header
   */
  calculateTotalAnnouncementHeight() {
    let totalHeight = 0;

    if (this.announcementBars.length === 0) return 0;

    // Get all sections in DOM order
    const allSections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
    const headerSection = this.header?.closest('[id^="shopify-section-"]');
    const headerIndex = headerSection ? allSections.indexOf(headerSection) : -1;

    if (headerIndex === -1) return 0;

    // Only count sticky announcement bars that appear BEFORE the header
    this.announcementBars.forEach(bar => {
      const sectionId = bar.dataset.sectionId;
      const section = document.getElementById(`shopify-section-${sectionId}`);

      if (section) {
        const barIndex = allSections.indexOf(section);
        // Only count if announcement bar comes before header AND is sticky
        if (barIndex < headerIndex && bar.dataset.sticky === 'true') {
          // Use the actual bar element height, not the section height
          // This ensures we only count the announcement bar itself, not any wrapper
          const barHeight = bar.offsetHeight || 0;
          totalHeight += barHeight;
        }
      }
    });

    return totalHeight;
  }

  /**
   * Recalculate announcement bar height and update header position
   */
  updateAnnouncementHeight() {
    // Re-query announcement bars in case new ones were added or sticky state changed
    this.announcementBars = document.querySelectorAll('[data-top-bar][data-sticky="true"]');
    const newHeight = this.announcementBars.length > 0 ? this.calculateTotalAnnouncementHeight() : 0;
    this.announcementBarHeight = newHeight || 0;
    // Only update position if header is currently sticky or always sticky
    const isSticky = this.header.classList.contains('header-sticky') || this.headerIsAlwaysSticky;
    if (isSticky) {
      this.setHeaderPosition(this.announcementBarHeight);
    }
  }

  /**
   * Close Search Modal on Window Scroll
   *
   */
  closeSearchModal() {
    let searchModal = document.querySelector('search-modal') || null;
    if (searchModal == null) return;

    if (searchModal.classList.contains('open__modal') && typeof searchModal.close == 'function') searchModal.close(false);
  }

  /**
   * Close Menu Dropdowns
   *
   */
  closeMenuDropdowns() {
    let openDropdowns = Array.from(document.querySelectorAll('.dropdown.open'));
    openDropdowns.forEach((ele) => {
      ele.classList.remove('open');
    });
  }
}
customElements.define('sticky-header', StickyHeader);